import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Save } from 'lucide-react';

const ContentTab = ({ content, setContent, saving, onSave }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">{t('siteSettings.siteContent', 'Site Content')}</h2>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
        </Button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('branding.siteName', 'Site Title')}</Label>
            <Input
              value={content.site_title || ''}
              onChange={(e) => setContent({ ...content, site_title: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('admin.siteSubtitle', 'Site Subtitle')}</Label>
            <Input
              value={content.site_subtitle || ''}
              onChange={(e) => setContent({ ...content, site_subtitle: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('admin.heroDescription', 'Hero Description')}</Label>
          <Textarea
            value={content.hero_description || ''}
            onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white min-h-24"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('admin.ctaButtonText', 'CTA Button Text')}</Label>
          <Input
            value={content.cta_button_text || ''}
            onChange={(e) => setContent({ ...content, cta_button_text: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('admin.footerDescription', 'Footer Description')}</Label>
          <Textarea
            value={content.footer_description || ''}
            onChange={(e) => setContent({ ...content, footer_description: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white min-h-24"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('admin.disclaimerText', 'Disclaimer Text')}</Label>
          <Textarea
            value={content.disclaimer || ''}
            onChange={(e) => setContent({ ...content, disclaimer: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white min-h-24"
            placeholder={t('admin.disclaimerPlaceholder', 'Legal disclaimer text shown at the bottom of listing detail pages')}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-300">{t('admin.aboutTemplate', 'About Template')}</Label>
            <button
              type="button"
              onClick={() => setContent({ ...content, about_template_enabled: !content.about_template_enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${content.about_template_enabled !== false ? 'bg-emerald-600' : 'bg-zinc-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${content.about_template_enabled !== false ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {content.about_template_enabled !== false && (
            <>
              <Textarea
                value={content.about_template || ''}
                onChange={(e) => setContent({ ...content, about_template: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white min-h-24"
                placeholder={t('admin.aboutTemplatePlaceholder', 'Available variables: {{category}}, {{city}}, {{count}}')}
              />
              <p className="text-zinc-500 text-xs">{t('admin.aboutTemplateHelp', 'Displayed in the "About" section on listing detail pages. Variables: {{category}} = category, {{city}} = city, {{count}} = project count')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentTab;
